import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import CampaignLabel from '../CampaignLabel'
import RecordsExplorer from '../RecordsExplorer'
import { useCodingBatchDetail } from '../../../hooks/useCodingBatchesPaged'
import HeldChip from '../HeldChip'
import { batchStateColor, phaseStepper, totalTokens } from '../../../utils/codingBatches'
import { formatImportTimestamp } from '../../../utils/importBatches'

/**
 * BatchDetailDialog — one coding batch, drilled into.
 *
 * Four things, in the order an operator asks for them: where it is in the phase
 * machine, what its current phase is waiting on, which records it carries (via
 * the SAME shared explorer every other door uses), and what it cost.
 *
 * The raw prompts and model response are realm-admin only and are stripped
 * SERVER-side. Nothing here checks a role: the sections render when the keys
 * are present, which is the only honest test of whether this caller may see
 * them.
 */

interface BatchDetailDialogProps {
  customer: string
  job: string
  batchId: string | null
  colorMap?: Record<string, string>
  onClose: () => void
}

const Facts: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
      {label}
    </Typography>
    <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
      {children}
    </Typography>
  </Box>
)

const CodeBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 2,
      bgcolor: 'action.hover',
      borderRadius: 1,
      overflow: 'auto',
      maxHeight: '40vh',
      fontSize: '0.75rem',
      whiteSpace: 'pre-wrap',
    }}
  >
    {children}
  </Box>
)

const BatchDetailDialog: React.FC<BatchDetailDialogProps> = ({
  customer,
  job,
  batchId,
  colorMap,
  onClose,
}) => {
  const { data, loading, error } = useCodingBatchDetail(customer, job, batchId || undefined)

  const batch = data?.batch
  const steps = phaseStepper(data?.phase)
  const summary = data?.request_summary
  const tokens = totalTokens(batch?.token_usage)
  const usageEntries = Object.entries(batch?.token_usage ?? {})

  return (
    <Dialog
      open={!!batchId}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { width: '90vw', maxWidth: '90vw', height: '85vh' } }}
    >
      <DialogTitle>
        Coding batch
        {batch && (
          <Typography variant="body2" color="text.secondary">
            {formatImportTimestamp(batch.created_at)} · {batch.model_version || 'no lane recorded'}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && !data && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress color="info" />
          </Box>
        )}

        {batch && (
          <>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Facts label="State">
                <Chip size="small" label={batch.state} color={batchStateColor(batch.state)} />
                <HeldChip batch={batch} />
              </Facts>
              <Facts label="Records">{batch.record_ids?.length?.toLocaleString() ?? '—'}</Facts>
              {/* Billed to. The endpoint has always sent the label bag — the
                  row is serialised whole — so this renders a fact that was on
                  the wire the entire time and simply had nothing reading it. */}
              <Facts label="Campaign">
                <CampaignLabel labels={batch.labels} />
              </Facts>
              <Facts label="Attempts">
                {batch.attempt_count}/{batch.max_attempts}
              </Facts>
              <Facts label="Tokens">{tokens === null ? '—' : tokens.toLocaleString()}</Facts>
              <Facts label="Started">
                {batch.started_at ? formatImportTimestamp(batch.started_at) : '—'}
              </Facts>
              <Facts label="Completed">
                {batch.completed_at ? formatImportTimestamp(batch.completed_at) : '—'}
              </Facts>
            </Stack>

            {/* A legacy batch has no phase machine — the old in-process drain
                never had one, so there is nothing honest to draw. */}
            {steps.length > 0 ? (
              <Stepper nonLinear sx={{ mb: 2 }}>
                {steps.map(step => (
                  <Step key={step.phase} completed={step.done} active={step.active}>
                    <StepLabel error={step.error}>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            ) : (
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                This batch predates the batch controller, so it carries no phase.
              </Alert>
            )}

            {/* The wait state of the CURRENT phase only: a batch between phases
                has emptied it, so all zeros means "waiting on nothing". */}
            {summary && (
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Typography variant="body2" color="text.secondary">
                  Model requests this phase is waiting on:
                </Typography>
                <Chip size="small" variant="outlined" label={`pending ${summary.pending}`} />
                <Chip size="small" variant="outlined" label={`running ${summary.running}`} />
                <Chip size="small" variant="outlined" label={`completed ${summary.completed}`} />
                <Chip
                  size="small"
                  variant="outlined"
                  color={summary.failed ? 'error' : 'default'}
                  label={`failed ${summary.failed}`}
                />
              </Stack>
            )}

            {batch.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {batch.error}
                </Typography>
              </Alert>
            )}

            {usageEntries.length > 0 && (
              <Accordion disableGutters elevation={0} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">Token usage</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {usageEntries.map(([key, value]) => (
                      <Chip
                        key={key}
                        size="small"
                        variant="outlined"
                        label={`${key.replace(/_/g, ' ')} · ${Number(value).toLocaleString()}`}
                      />
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Realm-admin only — present in the response or not at all. */}
            {data?.system_prompt && (
              <Accordion disableGutters elevation={0} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">System prompt</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CodeBlock>{data.system_prompt}</CodeBlock>
                </AccordionDetails>
              </Accordion>
            )}

            {data?.user_prompt && (
              <Accordion disableGutters elevation={0} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">User prompt</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CodeBlock>{data.user_prompt}</CodeBlock>
                </AccordionDetails>
              </Accordion>
            )}

            {data?.response_json !== undefined && data?.response_json !== null && (
              <Accordion disableGutters elevation={0} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">Response JSON</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CodeBlock>{JSON.stringify(data.response_json, null, 2)}</CodeBlock>
                </AccordionDetails>
              </Accordion>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Records in this batch
            </Typography>
            {batchId && (
              <RecordsExplorer
                customer={customer}
                job={job}
                door={{ codingBatchId: batchId }}
                colorMap={colorMap}
                emptyMessage="No records are linked to this batch."
              />
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button color="info" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BatchDetailDialog
